import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { userHasRight } from "selectors/rights";
import { fieldRepEmployeeColDefs, transformEmployeeList } from "components/Shared/TimeLogs/Definitions";
import { getPayPeriodList } from "requests/casetimelog";
import { MSpinner, MSplitter } from "components/Shared";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import moment from "moment-timezone";
import { getEmployeesByRoles } from "requests/employee";
import * as employeeRoles from "typeConstants/employeeRoles";
import { getInspectorListSummary } from "requests/inspectorTimeCard";
import MGrid, { ACTION_CONFIGURE, ACTION_DOWNLOAD } from "components/Shared/MGrid";
import MCard from "components/Shared/MCard.tsx";
import FieldRepTimeLogDetails from "components/EmployeeTimeLogs/FieldRepTimeLogDetails";
import MSectionCollection, { MSECTION_MODE_TAB } from "components/Shared/MSectionCollection";
import { approvePayPeriodUser } from "requests/account";
import { InspectorTimeCardResponse, PayPeriodViewModel } from "interfaces/Mueller.Business.Models";
import { TIME_REVIEW_DOWNLOAD } from "typeConstants/rights";

// this defines the columns for each section and their orders
const sectionDefinitions = [
  {
    title: "Payroll View",
    columns: [ "isTrainee", "name", "logisticsManager", "fieldRepTimeEfficiencyGradeLetter", "approvedIcon", "weeksEmployed", "state", "expectedFieldServiceRevenue", "totalHours", "adminHours", "actualCost", "nonProdCosts", "totalCost", "expectedCost", "fieldProductionCostDifference", "inspectorCostEfficiency" ],
  },
  {
    title: "Inventory View",
    columns: [ "isTrainee", "name", "logisticsManager", "fieldRepTimeEfficiencyGradeLetter", "approvedIcon", "weeksEmployed", "state", "specialtyCasesOverDuePercent", "appointmentCasesOverDuePercent", "exteriorCasesOverDuePercent", "lastCheckInDays", "lastEmployeeNote" ],
  },
  {
    title: "Coaching View",
    columns: null, // all columns
  },
];

interface MenuItem {
  label: string;
  value: string | boolean | PayPeriodViewModel;
};
interface MenuButtonDropdownProps {
  defaultText: string;
  items: MenuItem[];
  selectedItem: MenuItem;
  onChange: (item: MenuItem) => void;
  hasClearOption?: boolean;
};
const MenuButtonDropdown = ({ defaultText, items, selectedItem, onChange, hasClearOption = true } : MenuButtonDropdownProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  let text = defaultText;
  if (selectedItem) text = selectedItem.label;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleItemClick = (item: MenuItem | null) => {
    onChange(item);
    handleClose();
  };

  return (
    <>
      <Button
        onClick={handleClick}
        endIcon={<Icon>arrow_drop_down</Icon>}
      >
        {text}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {hasClearOption && <MenuItem onClick={() => handleItemClick(null)}>Clear Selection</MenuItem>}
        {items?.map((it, index) => (
          <MenuItem key={index} onClick={() => handleItemClick(it)}>
            {it.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

interface Props {
  // redux
  canDownload: boolean;
}

const FieldRepTimeCardPage = ({canDownload} : Props) => {
  const [payPeriods, setPayPeriods] = useState<MenuItem[]>(null);
  const [selectedPayPeriod, setSelectedPayPeriod] = useState(null);
  const [data, setData] = useState<InspectorTimeCardResponse[]>(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [fieldDevManagers, setFieldDevManagers] = useState(null);
  const [fieldOpsDirectors, setFieldOpsDirectors] = useState(null);
  const [fieldOpsManagers, setFieldOpsManagers] = useState(null);
  const [selectedFieldDev, setSelectedFieldDev] = useState(null);
  const [selectedFieldOpsDirector, setSelectedFieldOpsDirector] = useState(null);
  const [selectedFieldOpsManager, setSelectedFieldOpsManager] = useState(null);
  const [isTrainee, setIsTrainee] = useState(null);
  const [isApproving, setIsApproving] = useState(false);
  const [dataLastChanged, setDataLastChanged] = useState<number>(0);

  const mounted = useRef(true);

  useEffect(() => {
    getPayPeriodList(periods => setPayPeriods(periods.map(pp => ({ label: pp.fromToDisplay, value: pp }))), mounted);

    getEmployeesByRoles([employeeRoles.FIELD_DEVELOPMENT_MANAGER], employees => setFieldDevManagers(mapEmployeeList(employees)), mounted); // FDM
    getEmployeesByRoles([employeeRoles.FIELD_OPERATIONS_DIRECTOR], employees => setFieldOpsDirectors(mapEmployeeList(employees)), mounted); // Logistics Manager - call it Field Manager (chris says so)
    getEmployeesByRoles([employeeRoles.FIELD_OPERATIONS_MANAGER], employees => setFieldOpsManagers(mapEmployeeList(employees)), mounted); // Field Director

    return () => { mounted.current = false; };
  }, []);

  const mapEmployeeList = list => list.map(e => ({ label: e.firstLastName, value: e }));

  const hasEnoughFilters = () => selectedFieldDev || selectedFieldOpsDirector || selectedFieldOpsManager;

  const getInspectorListSummaryWrapped = (clearDataAndSelection = true, callback = null) => {
    let fieldDevManager = selectedFieldDev?.value.internetUserId;
    let fieldOpsDirector = selectedFieldOpsDirector?.value.internetUserId;
    let fieldOpsManager = selectedFieldOpsManager?.value.internetUserId;

    if (hasEnoughFilters()) {
      if (clearDataAndSelection) {
        setData(null);
      }
      const getSummaryCallback = data => {
        setData(data.sort((d1, d2) => d1.employeeName.localeCompare(d2.employeeName)));
        if (callback) callback();
      };
      getInspectorListSummary(selectedPayPeriod.value.id, fieldOpsManager, fieldOpsDirector, fieldDevManager, isTrainee, [employeeRoles.FIELD_SERVICE_REP], getSummaryCallback, mounted);
    }
  };

  useEffect(() => {
    getInspectorListSummaryWrapped();
  }, [selectedPayPeriod, selectedFieldDev, selectedFieldOpsDirector, selectedFieldOpsManager, isTrainee]);

  useEffect(() => {
    setDataLastChanged(Date.now()); // lets the grid refresh its data
  }, [data]);

  useEffect(() => {
    if (!selectedPayPeriod && payPeriods?.length) {
      setSelectedPayPeriod(payPeriods[0]);
    }
  }, [payPeriods]);

  const handleApproveClick = () => {
    let selectedInternetUserId = selectedRow?.internetUserId;
    if (selectedInternetUserId) {
      setIsApproving(true);
      approvePayPeriodUser(selectedPayPeriod.value.id, selectedInternetUserId, () => getInspectorListSummaryWrapped(false, () => setIsApproving(false)), mounted);
    }
  };

  let disableApprove = true;
  if (selectedPayPeriod) {
    // approve per grid row
    const { closedDate, toDate } = selectedPayPeriod.value;
    if (!closedDate &&
        selectedRow?.isApproved === false &&
        moment().diff(moment({ year: toDate.year, month: toDate.month - 1, day: toDate.day })) > 0
    ) {
      disableApprove = false;
    }
  }

  const traineeOptions: MenuItem[] = [
    { label: "Is Trainee", value: true },
    { label: "Is Not Trainee", value: false },
  ];
  const traineeValue = isTrainee === null ? null : traineeOptions[1 - isTrainee]; // math on a boolean? fight me

  let inner = null;
  if (payPeriods) {
    inner = [
      <div className="header-buttons" key="headerButtons">
        <MenuButtonDropdown defaultText="Select a Pay Period" items={payPeriods} selectedItem={selectedPayPeriod} onChange={setSelectedPayPeriod} />
        <Button
          className="mark-reviewed"
          onClick={handleApproveClick}
          disabled={disableApprove || isApproving}
        >
          Mark Reviewed
        </Button>
        <MenuButtonDropdown defaultText="Field Director" items={fieldOpsDirectors} selectedItem={selectedFieldOpsDirector} onChange={setSelectedFieldOpsDirector} />
        <MenuButtonDropdown defaultText="Field Manager" items={fieldOpsManagers} selectedItem={selectedFieldOpsManager} onChange={setSelectedFieldOpsManager} />
        <MenuButtonDropdown defaultText="FDM" items={fieldDevManagers} selectedItem={selectedFieldDev} onChange={setSelectedFieldDev} />
        <MenuButtonDropdown defaultText="Trainee?" items={traineeOptions} selectedItem={traineeValue} onChange={selection => setIsTrainee(selection?.value)} />
      </div>
    ];

    const getMGrid = (colKeys, gridKey) => {
      const colDefs = fieldRepEmployeeColDefs(data);
      const filteredOrderedColDefs = colKeys ? colKeys.map(k => colDefs.find(cd => cd.key === k)) : colDefs;
      const actions = [ACTION_CONFIGURE];
      if (canDownload)
        actions.push(ACTION_DOWNLOAD);

      return (
        <MGrid
          key={gridKey} // need a different key for each tab otherwise react will reuse the grid on tab change and things will be wrong
          columnDefinitions={filteredOrderedColDefs}
          data={transformEmployeeList(data)}
          dataLastChanged={dataLastChanged}
          isFetchingData={!data && hasEnoughFilters()}
          displayNumOfItemsFound={false}
          onRowClick={i => setSelectedRow(data[i])} // going off of the entire row instead of index because we want to have that data persist when you click a new time period and the index might be inacurrate at that point
          headerText="Field Rep TimeLogs"
          actions={actions}
        />
      );
    };

    if (hasEnoughFilters()) {
      let sections = sectionDefinitions.map(s => ({
        title: s.title,
        children: getMGrid(s.columns, s.title),
      }));

      inner.push(
        <MSectionCollection
          key="sectionCollection"
          defaultMode={MSECTION_MODE_TAB}
          disableModeChanging
          fullHeight
          sections={sections}
          tabModeCollapsible={false}
        />
      );
    }
  } else {
    inner = <MSpinner />;
  }

  let selectedEmployee = null;

  // we can have a selected row without data because they want the row selection to persist through changing the dropdown dates
  if (selectedRow) {
    selectedEmployee = {
      internetUserId: selectedRow.internetUserId,
      name: selectedRow.employeeName,
      number: selectedRow.inspectorNumber,
    };
  }

  return (
    <MSplitter
      className="field-rep-time-card-page full-height"
      firstChild={
        <MCard className="employee-list" title="Employee List">
          {inner}
        </MCard>
      }
      secondChildTitle={{
        title: "Time Log",
        buttons: [
          { icon: "close", onClick: () => setSelectedRow(null) }
        ]
      }}
      secondChild={selectedEmployee !== null && <FieldRepTimeLogDetails selectedPayPeriod={selectedPayPeriod.value} selectedEmployee={selectedEmployee} />}
    />
  );
};

const mapStateToProps = state => {
  return {
    canDownload: userHasRight(TIME_REVIEW_DOWNLOAD)(state),
  };
};

export default connect(mapStateToProps)(FieldRepTimeCardPage);
